// EntidadDialog — crear / editar los niveles superiores de la estructura:
// Confederación Internacional, Confederación Nacional y Federación. Los
// campos son los que persisten las tablas (migración 010), basados en lo que
// Ecolab/Fenagro ya guarda de cada nivel. La cooperativa tiene su propio
// asistente (CooperativaWizardModal).
//
// Reglas de UI:
//   • País / departamento / municipio salen SIEMPRE del catálogo de
//     geografía (SelectorGeografia), nunca de texto libre.
//   • Confederación nacional: el país y la confederación internacional a la
//     que pertenece son obligatorios (así se sostiene la jerarquía).
//   • Al editar aparece el interruptor "Activa".

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import SelectorGeografia from './SelectorGeografia';

const CAMPOS = {
  internacional: {
    titulo: 'confederación internacional',
    cortos: [
      { campo: 'nombre', label: 'Nombre *' },
      { campo: 'siglas', label: 'Siglas' },
      { campo: 'nit', label: 'NIT' },
      { campo: 'representanteLegal', label: 'Representante legal' },
      { campo: 'email', label: 'Correo electrónico', tipo: 'email' },
      { campo: 'telefono', label: 'Teléfono' },
      { campo: 'sitioWeb', label: 'Sitio web' },
      { campo: 'fechaConstitucion', label: 'Fecha de constitución', tipo: 'date' },
    ],
    largos: [
      { campo: 'descripcion', label: 'Descripción' },
      { campo: 'mision', label: 'Misión' },
      { campo: 'vision', label: 'Visión' },
    ],
  },
  confederacion: {
    titulo: 'confederación nacional',
    cortos: [
      { campo: 'nombre', label: 'Nombre *' },
      { campo: 'siglas', label: 'Siglas' },
      { campo: 'nit', label: 'NIT' },
      { campo: 'representanteLegal', label: 'Representante legal' },
      { campo: 'email', label: 'Correo electrónico', tipo: 'email' },
      { campo: 'telefono', label: 'Teléfono' },
      { campo: 'sitioWeb', label: 'Sitio web' },
      { campo: 'fechaConstitucion', label: 'Fecha de constitución', tipo: 'date' },
    ],
    largos: [{ campo: 'descripcion', label: 'Descripción' }],
  },
  federacion: {
    titulo: 'federación',
    cortos: [
      { campo: 'nombre', label: 'Nombre *' },
      { campo: 'siglas', label: 'Siglas' },
      { campo: 'especializacion', label: 'Especialización (FENAGRO, SEMPRE, Turred…)' },
      { campo: 'nit', label: 'NIT' },
      { campo: 'representanteLegal', label: 'Representante legal' },
      { campo: 'cedulaRepresentante', label: 'Cédula del representante' },
      { campo: 'email', label: 'Correo electrónico', tipo: 'email' },
      { campo: 'telefono', label: 'Teléfono' },
      { campo: 'sitioWeb', label: 'Sitio web' },
      { campo: 'fechaConstitucion', label: 'Fecha de constitución', tipo: 'date' },
    ],
    largos: [{ campo: 'descripcion', label: 'Descripción' }],
  },
};

const aTexto = (v) => (v === null || v === undefined ? '' : String(v));
const aEntero = (v) => (v === '' || v === null || v === undefined ? null : parseInt(v, 10));

/**
 * @param {boolean} open
 * @param {'internacional'|'confederacion'|'federacion'} nivel
 * @param {'crear'|'editar'} modo
 * @param {object|null} inicial  entidad a editar
 * @param {object[]} internacionales  para elegir el padre de una confederación nacional
 * @param {string|null} padreInicial  internacional preseleccionada (al crear una confederación)
 * @param {(dto: object) => Promise<void>} onGuardar  debe lanzar Error si falla (se muestra en el diálogo)
 * @param {() => void} onClose
 */
export default function EntidadDialog({ open, nivel, modo = 'crear', inicial = null, internacionales = [], padreInicial = null, onGuardar, onClose }) {
  const cfg = CAMPOS[nivel] || CAMPOS.federacion;
  const editar = modo === 'editar';
  const todos = useMemo(() => [...cfg.cortos, ...cfg.largos], [cfg]);

  const [form, setForm] = useState({});
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setError('');
    const base = Object.fromEntries(todos.map((c) => [c.campo, aTexto(inicial?.[c.campo])]));
    setForm({
      ...base,
      direccion: aTexto(inicial?.direccion),
      paisId: inicial?.paisId ?? '',
      departamentoId: inicial?.departamentoId ?? '',
      municipioId: inicial?.municipioId ?? '',
      pais: aTexto(inicial?.pais),
      activa: inicial ? inicial.activa !== false : true,
      confederacionInternacionalId: inicial?.confederacionInternacionalId || padreInicial || '',
    });
  }, [open, inicial, padreInicial, todos]);

  const set = (parcial) => setForm((f) => ({ ...f, ...parcial }));

  const esConfederacion = nivel === 'confederacion';
  const valido =
    Boolean(aTexto(form.nombre).trim()) && (!esConfederacion || (Boolean(form.paisId) && Boolean(form.confederacionInternacionalId)));

  const guardar = async () => {
    setEnviando(true);
    setError('');
    try {
      const dto = {
        ...Object.fromEntries(todos.map((c) => [c.campo, form[c.campo]])),
        fechaConstitucion: form.fechaConstitucion || null,
        direccion: form.direccion,
        paisId: aEntero(form.paisId),
        departamentoId: aEntero(form.departamentoId),
        municipioId: aEntero(form.municipioId),
      };
      if (esConfederacion) {
        dto.pais = form.pais;
        dto.confederacionInternacionalId = form.confederacionInternacionalId || null;
      }
      if (editar) dto.activa = form.activa;
      await onGuardar(dto);
    } catch (e) {
      setError(e?.message || 'No se pudo guardar.');
    } finally {
      setEnviando(false);
    }
  };

  const campo = (c) => (
    <TextField
      key={c.campo}
      label={c.label}
      type={c.tipo || 'text'}
      value={form[c.campo] ?? ''}
      onChange={(e) => set({ [c.campo]: e.target.value })}
      InputLabelProps={c.tipo === 'date' ? { shrink: true } : undefined}
      fullWidth
    />
  );

  const grid = { display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 };

  return (
    <Dialog open={open} onClose={enviando ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        {editar ? 'Editar' : 'Nueva'} {cfg.titulo}
      </DialogTitle>

      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {error && (
          <Alert severity="error" onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {esConfederacion && (
          <FormControl fullWidth required error={!form.confederacionInternacionalId}>
            <InputLabel>Confederación internacional *</InputLabel>
            <Select
              label="Confederación internacional *"
              value={internacionales.some((i) => i.id === form.confederacionInternacionalId) ? form.confederacionInternacionalId : ''}
              onChange={(e) => set({ confederacionInternacionalId: e.target.value })}
            >
              {internacionales.map((i) => (
                <MenuItem key={i.id} value={i.id}>
                  {i.nombre}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <Box sx={grid}>{cfg.cortos.map(campo)}</Box>

        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Ubicación {esConfederacion ? '(el país es obligatorio)' : ''}
          </Typography>
          <SelectorGeografia
            nivel="municipio"
            valor={{ paisId: form.paisId, departamentoId: form.departamentoId, municipioId: form.municipioId }}
            onChange={(g) =>
              set({ paisId: g.paisId, departamentoId: g.departamentoId, municipioId: g.municipioId, pais: g.paisNombre })
            }
            etiquetas={{ pais: esConfederacion ? 'País *' : 'País' }}
          />
          <Box sx={{ mt: 2 }}>
            <TextField label="Dirección" value={form.direccion ?? ''} onChange={(e) => set({ direccion: e.target.value })} fullWidth />
          </Box>
        </Box>

        {cfg.largos.map((c) => (
          <TextField
            key={c.campo}
            label={c.label}
            value={form[c.campo] ?? ''}
            onChange={(e) => set({ [c.campo]: e.target.value })}
            multiline
            minRows={2}
            fullWidth
          />
        ))}

        {editar && (
          <FormControlLabel
            control={<Switch checked={Boolean(form.activa)} onChange={(e) => set({ activa: e.target.checked })} />}
            label="Activa"
          />
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={enviando}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={guardar}
          disabled={!valido || enviando}
          startIcon={enviando ? <CircularProgress size={18} color="inherit" /> : null}
        >
          {editar ? 'Guardar cambios' : 'Crear'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
